/**
 * Branded billing card surfaces lifted from Figma (file Z1l7MGwOoVygv0LP7KMG97):
 * a 1px outline + diagonal gradient fill, radius 6.
 *
 * Border colours map to DS primitives: purple-700 (#39277F) and red-700 (#611D30).
 * The rgba gradient stops have no primitive equivalent and stay as arbitrary values.
 *
 *   trial  → border-purple-700, gradient rgba(30,27,57,0.4)→rgba(20,20,20,0.4)
 *   danger → border-red-700,    gradient rgba(97,29,48,0.2)→rgba(20,20,20,0.2)
 */
export type BillingSurfaceTone = 'trial' | 'danger';

/** Value-const for the {@link BillingSurfaceTone} union. */
export const BILLING_SURFACE_TONES = {
  trial: 'trial',
  danger: 'danger',
} as const satisfies Record<string, BillingSurfaceTone>;

/** Card surface (outline + gradient fill) for the hero + sidebar widget. */
export const BILLING_SURFACE: Record<BillingSurfaceTone, string> = {
  trial:
    'border border-purple-700 bg-[linear-gradient(127deg,rgba(30,27,57,0.4)_0%,rgba(20,20,20,0.4)_100%)]',
  danger: 'border border-red-700',
};

/** Pill outline only (transparent fill) — same hues as the card surfaces. */
export const BILLING_PILL_BORDER: Record<BillingSurfaceTone, string> = {
  trial: 'border-purple-700',
  danger: 'border-red-700',
};

/**
 * "Danger" accent text for the trial-ending + hard-block surfaces.
 * Uses DS semantic token text-general-negative-mid (red-500 light / red-400 dark)
 * for both icon and label, and DS primitive red-500 for the separator dot.
 */
export const BILLING_DANGER = {
  text: 'text-text-general-negative-mid',
  dot: 'bg-icon-status-emphasis-negative',
} as const;

/**
 * Neutral text shades used across billing surfaces, all resolved via DS tokens:
 *   muted        → text-general-neutral-subtle   (purple-muted-350 = #919191)
 *   mutedStrong  → text-general-neutral-muted     (purple-muted-400 = #6e6e6e)
 *   offWhite     → purple-muted-100 primitive      (#f4f4f4)
 */
export const BILLING_TEXT = {
  muted: 'text-text-general-neutral-subtle',
  mutedStrong: 'text-text-general-neutral-muted',
  offWhite: 'text-purple-muted-100',
} as const;

/**
 * "Overage" accent text for the trial-ending + hard-block surfaces.
 * Uses DS semantic token text-general-negative-mid (red-500 light / red-400 dark)
 * for both icon and label, and DS primitive red-500 for the separator dot.
 */
export const BILLING_OVERAGE = {
  text: 'text-general-negative-mid',
  pillText: 'text-orange-400',
  border: 'border-orange-600',
} as const;

/**
 * Low-emphasis CTA inside the overage surfaces (pill + banner): muted grey label
 * and chevron — NOT the purple action colour the trial/blocked CTAs use.
 */
export const BILLING_OVERAGE_CTA = {
  label: 'text-text-action-neutral-idle hover:text-foreground',
  chevron: 'text-text-general-neutral-muted',
} as const;
